const generateId = () => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

interface SubContent {
  id: string;
  title: string;
  content: string;
  children?: string;
}

interface Content {
  id: string;
  title: string;
  richText: any | null;
  content: string;
  subContent: SubContent[];
}

interface DocumentData {
  id: string;
  updatedOn: string;
  version: string;
  heading: string;
  blockName: null;
  content: Content[];
  blockType: string;
}

interface CreateSubContentOptions {
  title: string;
  content?: string;
  listItems?: string[];
  html?: string;
}

export const createSubContent = ({
  title,
  content = "",
  listItems,
  html,
  sectionId,
  subSectionId,
}: CreateSubContentOptions & { sectionId?: string; subSectionId?: string }): SubContent => ({
  id: subSectionId ? `section-${sectionId}-${subSectionId}` : generateId(),
  title: title ? `${sectionId}.${subSectionId} ${title}` : "",
  content: content,
  children: html
    ? html
    : listItems
      ? `<ul class="list-disc pl-4">${listItems.map(item => `<li>${item}</li>`).join("")}</ul>`
      : undefined,
});

export const createRichText = (text: string, listItems?: string[]) => {
  const children = [];

  if (text) {
    children.push({
      type: "paragraph",
      format: "",
      indent: 0,
      version: 1,
      children: [
        {
          mode: "normal",
          text,
          type: "text",
          style: "",
          detail: 0,
          format: 0,
          version: 1,
        },
      ],
      direction: "ltr",
      textStyle: "",
      textFormat: 0,
    });
  }

  if (listItems) {
    children.push({
      tag: "ul",
      type: "list",
      start: 1,
      format: "",
      indent: 0,
      version: 1,
      children: listItems.map((item, index) => ({
        type: "listitem",
        value: index + 1,
        format: "start",
        indent: 0,
        version: 1,
        children: [
          {
            mode: "normal",
            text: item,
            type: "text",
            style: "",
            detail: 0,
            format: 0,
            version: 1,
          },
        ],
        direction: "ltr",
      })),
      listType: "bullet",
      direction: "ltr",
    });
  }

  return {
    root: {
      type: "root",
      format: "",
      indent: 0,
      version: 1,
      children,
      direction: "ltr",
    },
  };
};

export const createContentBlock = (
  title: string,
  content: string = "",
  subContent: SubContent[] = [],
  listItems?: string[],
  sectionNumber?: number
): Content => {
  const sectionId = sectionNumber ? `section-${sectionNumber}` : generateId();

  return {
    id: sectionId,
    title: title,
    richText: createRichText(content, listItems),
    content: content
      ? listItems
        ? `<p>${content}</p><ul class="list-bullet">${listItems.map(item => `<li>${item}</li>`).join("")}</ul>`
        : `<p>${content}</p>`
      : "",
    subContent: subContent.map((item, index) => ({
      ...item,
      id: item.id.includes("section-") ? item.id : `${sectionId}-${index + 1}`,
    })),
  };
};

export const createDocument = (
  type: "privacy-policy" | "terms-of-service",
  heading: string,
  content: Content[],
  version: string = "0.02"
): DocumentData => ({
  id: generateId(),
  updatedOn: new Date().toISOString(),
  version,
  heading,
  blockName: null,
  content,
  blockType: type,
});
